import { useEffect } from "react";

/**
 * Client-side document meta manager. Sets title + upserts standard SEO tags
 * (description, canonical, Open Graph, Twitter) and optional JSON-LD.
 *
 * SPA note: we don't have SSR, so this only affects the *live* document after
 * hydration. Modern crawlers (Googlebot, Twitterbot, LinkedIn) execute JS and
 * will pick these up; older/scrape-only bots (Facebook, sometimes) may not.
 * For those, a prerender step would be the follow-up.
 */
export default function useDocumentMeta({
  title,
  description,
  canonical,
  ogImage,
  ogType = "article",
  jsonLd,
} = {}) {
  useEffect(() => {
    if (typeof document === "undefined") return;
    const prevTitle = document.title;
    if (title) document.title = title;

    const setMeta = (attr, key, value) => {
      if (!value) return null;
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", value);
      return el;
    };

    const created = [];
    created.push(setMeta("name", "description", description));
    created.push(setMeta("property", "og:title", title));
    created.push(setMeta("property", "og:description", description));
    created.push(setMeta("property", "og:image", ogImage));
    created.push(setMeta("property", "og:type", ogType));
    created.push(setMeta("property", "og:url", canonical));
    created.push(setMeta("name", "twitter:card", ogImage ? "summary_large_image" : "summary"));
    created.push(setMeta("name", "twitter:title", title));
    created.push(setMeta("name", "twitter:description", description));
    created.push(setMeta("name", "twitter:image", ogImage));

    // Canonical link tag
    let linkEl = null;
    if (canonical) {
      linkEl = document.head.querySelector('link[rel="canonical"]');
      if (!linkEl) {
        linkEl = document.createElement("link");
        linkEl.setAttribute("rel", "canonical");
        document.head.appendChild(linkEl);
      }
      linkEl.setAttribute("href", canonical);
    }

    // JSON-LD structured data
    let jsonLdEl = null;
    if (jsonLd) {
      jsonLdEl = document.createElement("script");
      jsonLdEl.type = "application/ld+json";
      jsonLdEl.text = JSON.stringify(jsonLd);
      jsonLdEl.setAttribute("data-doc-meta", "1");
      document.head.appendChild(jsonLdEl);
    }

    return () => {
      document.title = prevTitle;
      if (jsonLdEl && jsonLdEl.parentNode) jsonLdEl.parentNode.removeChild(jsonLdEl);
      // We intentionally leave meta/canonical in place — next useDocumentMeta
      // call overwrites them.
    };
  }, [title, description, canonical, ogImage, ogType, jsonLd]);
}
